import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import PropertyCard from '../components/PropertyCard';
import { JsonLd, faqSchema, breadcrumbSchema } from '../components/JsonLd';
import {
    developerStats, developerIsIndexable, developerTitle, developerDescription, developerAnswer, developerFaq, developerLabels
} from '../config/developers';
import { collectionNode } from '../config/schema';
import { NAP } from '../config/nap';
import { LANGS, isRtl as rtl, localizedPath } from '../config/langs';
import { ui } from '../config/seoCopy';
import { useBuildData } from '../utils/buildData';

/**
 * /developer/:slug — every project of one developer.
 *
 * Built from /data/developer/<slug>.json, the record the prerenderer wrote
 * this page's static HTML from, so the hydrated page matches it line for line.
 */
export default function DeveloperPage() {
    const { slug } = useParams();
    const { t, i18n } = useTranslation();
    const base = (i18n.language || 'ar').split('-')[0];
    const lang = LANGS.includes(base) ? base : 'en';
    const L = developerLabels(lang);
    const U = ui(lang);
    const { state, data } = useBuildData(`developer/${encodeURIComponent(slug)}.json`);

    const dev = data?.developer;
    const rows = useMemo(() => data?.rows || [], [data]);
    const stats = useMemo(() => developerStats(rows), [rows]);

    if (state === 'loading' || state === 'error') {
        return (
            <div style={{ minHeight: '100vh' }}>
                <Header />
                <main className="container" style={{ padding: '160px 1rem 5rem', color: '#999' }}>{t('projects.loading')}</main>
            </div>
        );
    }
    if (!dev) {
        return (
            <div style={{ minHeight: '100vh' }}>
                <SEO title={L.notFound} path={`/developer/${slug}`} noindex />
                <Header />
                <main className="container" style={{ padding: '160px 1rem 5rem', textAlign: 'center' }}>
                    <h1 style={{ color: '#fff' }}>{L.notFound}</h1>
                    <Link to="/developers" style={{ color: 'var(--color-gold)' }}>{L.all}</Link>
                </main>
            </div>
        );
    }

    const url = `${NAP.url}${localizedPath(`/developer/${dev.slug}`, lang)}`;
    const title = stats.liveCount ? developerTitle(dev, stats, lang) : L.h1(dev);
    const faq = developerFaq(dev, stats, lang);
    const live = new Set(stats.live.map((p) => String(p.id)));
    const price = (r) => Number(r.min_price) || Infinity;
    const ordered = [...rows].sort((a, b) => (live.has(String(b.id)) - live.has(String(a.id))) || price(a) - price(b));
    const crumbs = [{ name: U.home, to: '/' }, { name: U.developers, to: '/developers' }, { name: dev.name }];
    const enOnly = lang === 'en' ? {} : { lang: 'en', dir: 'ltr' };

    return (
        <div style={{ minHeight: '100vh' }} dir={rtl(lang) ? 'rtl' : 'ltr'}>
            <SEO
                fullTitle={title}
                description={stats.liveCount ? developerDescription(dev, stats, lang) : L.h1(dev)}
                path={`/developer/${dev.slug}`}
                image={dev.logo || undefined}
                noindex={!developerIsIndexable(stats)}
            />
            <JsonLd data={collectionNode({
                url, lang, name: title,
                description: developerDescription(dev, stats, lang),
                about: { '@type': 'Organization', name: dev.name, ...(dev.logo && { logo: dev.logo }) },
                items: stats.live
            })} />
            {faq.length > 0 && <JsonLd data={faqSchema(faq, { url, lang })} />}
            <JsonLd data={breadcrumbSchema(crumbs.map((c) => ({ name: c.name, url: c.to || `/developer/${dev.slug}` })))} />
            <Header />

            <main className="container" style={{ padding: '140px 1rem 4rem' }}>
                <Breadcrumbs items={crumbs} />
                <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 800, color: '#fff', lineHeight: 1.25, margin: '0 0 1rem' }}>
                    {L.h1(dev)}
                </h1>
                {stats.liveCount > 0 && (
                    <div style={{ background: 'rgba(212,175,55,.07)', borderInlineStart: '3px solid var(--color-gold)', borderRadius: 8, padding: '1.25rem 1.5rem', color: '#eee', lineHeight: 1.9, maxWidth: 860, marginBottom: '1.5rem' }}>
                        <p style={{ margin: 0 }}>{developerAnswer(dev, stats, lang)}</p>
                    </div>
                )}
                <a href={`https://wa.me/${NAP.whatsapp}`} target="_blank" rel="noopener noreferrer" className="btn-primary"
                    style={{ display: 'inline-block', marginBottom: '2rem', textDecoration: 'none' }}>
                    {L.cta(dev)}
                </a>

                {dev.description?.length > 0 && (
                    <section className="glass-card" style={{ padding: '2rem', marginBottom: '2rem', maxWidth: 860 }}>
                        <h2 style={{ color: 'var(--color-gold)', fontSize: '1.4rem', margin: '0 0 1rem' }}>{L.about(dev)}</h2>
                        <div {...enOnly} style={{ color: '#bbb', lineHeight: 1.8 }}>
                            {dev.description.map((d) => <p key={d}>{d}</p>)}
                        </div>
                    </section>
                )}

                <h2 style={{ color: 'var(--color-gold)', fontSize: '1.4rem', margin: '1rem 0 1.25rem' }}>{L.list(dev, rows.length)}</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '2rem', marginBottom: '3rem' }}>
                    {ordered.map((p) => <PropertyCard key={p.id} project={p} />)}
                </div>

                {faq.length > 0 && (
                    <section>
                        <h2 style={{ color: 'var(--color-gold)', fontSize: '1.4rem', margin: '2rem 0 1rem' }}>{L.faq(dev)}</h2>
                        {faq.map((f) => (
                            <div key={f.q} style={{ maxWidth: 860 }}>
                                <h3 style={{ color: '#fff', fontSize: '1.05rem', margin: '1.5rem 0 .4rem' }}>{f.q}</h3>
                                <p style={{ color: '#bbb', lineHeight: 1.9, margin: 0 }}>{f.a}</p>
                            </div>
                        ))}
                    </section>
                )}

                <p style={{ marginTop: '2.5rem' }}>
                    <Link to="/developers" style={{ color: 'var(--color-gold)', textDecoration: 'none' }}>{L.all}</Link>
                </p>
            </main>

            <Footer />
        </div>
    );
}
